/* eslint-disable @nx/enforce-module-boundaries */
import { ChatOutLined, HandShakeSimpleIcon, SimpleCloseIcon, SkillIcon } from '@mybridge/icons';
import {
  Avatar,
  AvatarGroup,
  Box,
  Button,
  Center,
  HStack,
  Heading,
  Link,
  Spinner,
  Stack,
  Text,
  Tooltip,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  Portal,
} from '@mybridge/ui';
import { Divider } from '@mybridge/ui/divider';
import { forwardRef, useContext, useRef, useState } from 'react';
import { getUserFullName, getUserProfilePic } from 'v4/lib/commons';
import { PublicProfileContext } from '../context';
import { MyNetworkContext } from 'v4/components/network/context';
import { useRouter } from 'next/router';
import { Flex, IconButton, useDisclosure, VStack } from '@chakra-ui/react';
import { useTranslator } from '@mybridge/ui/custom-hooks/useTranslation';
import { useSelector } from 'react-redux';
import { GlobalMatrixPopup } from 'v4/snippets/globalMatrixPopup';
import { getMatrixUserId, getRoomIdFromLS } from 'v4/utils/utils';
import { ForceSignInWrapper } from 'v4/components/force-sign-in-wrapper';

export const MyProfileSkills = forwardRef(({ ...props }, ref) => {
  const {
    skills,
    publicProfile,
    publicProfileFetching,
    endorseSkill,
    selectedSkill,
    setSelectedSkill,
    endorsmentLoading,
    publicProfileLoading,
    userProfileInfo,
  } = useContext(PublicProfileContext);
  const { 
    isFriend,
    handshakeUser,
    isHandshakeRequested,
    handshakeLoading,
    handleHandshakeSendRevoke, 
  } = useContext(MyNetworkContext);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [nullMatrixUser, setNullMatrixUser] = useState(null);
  const [confirmAction, setConfirmAction] = useState(() => () => {});
  const [hoveredSkillId, setHoveredSkillId] = useState(null);
  const router = useRouter();
  const cardRefs = useRef({});
  const timerRef = useRef(null);
  const { t } = useTranslator();
  const { userProfileInfo: currentUser } = useSelector((state) => state.userProfile);

  const [loadingButtonId, setLoadingButtonId] = useState(null);
  const [showAllSkills, setShowAllSkills] = useState(false);

  // Check audience settings for skills
  const skillsAudience = publicProfile?.field_audience?.skills;
  
  // Check if skills should be visible based on audience settings
  // If audience settings don't exist, default to showing skills (treat as public)
  const shouldShowSkills = 
    publicProfile?.id === currentUser?.id || // Own profile
    skillsAudience === undefined || // No audience setting (default to visible)
    skillsAudience === 1 || // Public
    (skillsAudience === 2 && isFriend(publicProfile)); // Connections only and is a connection

  // Check if there are any skills to display
  const hasSkills = skills && skills.length > 0;

  // Check if this is the user's own profile
  const isOwnProfile = publicProfile?.id === currentUser?.id;

  // Determine if we should show the heading - similar to experience and education components
  const shouldShowHeading = shouldShowSkills && hasSkills;

  /* const getPopupPosition = (skillId) => {
    const cardElement = cardRefs.current[skillId];
    if (!cardElement) return {};
    
    const rect = cardElement.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const spaceAbove = rect.top;
    
    if (spaceAbove >= 400) {
      return {
        bottom: viewportHeight - rect.top + 8,
        left: rect.left
      };
    }
    
    return {
      top: rect.bottom + 8,
      left: rect.left
    };
  }; */

  const getPopupPosition = (skillId) => {
    const cardElement = cardRefs.current[skillId];
    if (!cardElement) return {};
    
    const rect = cardElement.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const spaceAbove = rect.top;
    
    const isMobileViewport = typeof window !== 'undefined' && window.innerWidth < 480;
    
    if (isMobileViewport) {
      if (spaceAbove >= 400) {
        return {
          bottom: viewportHeight - rect.top + 8,
          left: '16px',
          right: '16px',
          width: 'calc(100vw - 32px)'
        };
      }
      return {
        top: rect.bottom + 8,
        left: '16px',
        right: '16px',
        width: 'calc(100vw - 32px)'
      };
    }
    
    let leftPos = rect.left;
    if (leftPos + 400 > viewportWidth - 16) {
      leftPos = viewportWidth - 400 - 16;
    }
    if (leftPos < 16) {
      leftPos = 16;
    }

    if (spaceAbove >= 400) {
      return {
        bottom: viewportHeight - rect.top + 8,
        left: leftPos
      };
    }
    
    return {
      top: rect.bottom + 8,
      left: leftPos
    };
  };

  /* const handleMouseEnter = (skill) => { */
  const handleMouseEnter = (skill, e) => {
    if (e?.nativeEvent?.pointerType === 'touch' || e?.target?.closest?.('a, button')) return;
    if (skill?.agreed_user?.length > 0) {
      clearTimeout(timerRef.current);
      setHoveredSkillId(skill.id);
    }
  };

  const handleMouseLeave = () => {
    timerRef.current = setTimeout(() => {
      setHoveredSkillId(null);
    }, 1000);
  };

  const handleHandshake = async (user) => {
    setLoadingButtonId(user.id);
    try {
      await handshakeUser?.(user);
    } finally {
      setLoadingButtonId(null);
    }
  };

  const { id } = userProfileInfo ?? {};

  return (
    <Stack>
      {/* Only show heading if we have skills to display */}
      {/* {shouldShowHeading && ( */}
        <HStack alignItems="flex-start">
          <Stack flex={1}>
            <Heading fontSize="18px" color="brandPrimary.500" fontWeight="400">
              {t('profileSkills.skills') || "Skills"}
            </Heading>
          </Stack>
        </HStack>
      {/* )} */}

      {hasSkills && shouldShowSkills ? (
        <>
          <HStack alignItems="flex-start" flexWrap={'wrap'} pt={4} spacing={0} gap={4}>
            {(showAllSkills ? skills : skills?.slice(0, 5))?.map?.((skill, skillIndex) => {
              const likedBycurrentUser = skill?.agreed_user?.some(
                (item) => item.id === id
              );
              return (
                <Stack
                  key={skillIndex}
                  ref={(el) => (cardRefs.current[skill.id] = el)}
                  px={4}
                  py={2}
                  bg="brandGray.500"
                  borderRadius={10}
                  spacing={0}
                  gap={1}
                  onMouseEnter={(e) => handleMouseEnter(skill, e)}
                  onMouseLeave={handleMouseLeave}
                  cursor="pointer"
                >
                  <Text>{skill.name}</Text>
                  <ForceSignInWrapper>
                  <HStack role={'group'}> 
                    {(
                      <Box>
                        {skill?.id === selectedSkill?.id &&
                        (endorsmentLoading || publicProfileFetching) ? (
                          <Spinner size="xs" />
                        ) : (
                          <Tooltip
                            label={
                              !likedBycurrentUser
                                ? `${t('publicProfile.agreeWith') || "Agree with"} ${publicProfile?.first_name} ${t('publicProfile.onThis') || "on this?"}`
                                : `${t('publicProfile.withdrawAgreement') || "Withdraw your agreement with"} ${publicProfile?.first_name} ${t('publicProfile.onThisStatement') || "on this"}`
                            }
                          >
                            <Box
                              as={Link}
                              onClick={(e) => {
                                e.stopPropagation();
                                setHoveredSkillId(null);
                                setSelectedSkill(skill);
                                endorseSkill?.(skill);
                              }}
                            >
                              <HandShakeSimpleIcon
                                width={20}
                                height={20}
                                color={!likedBycurrentUser ? "rgb(148, 176, 150)" : '#037F29'}
                              />
                            </Box>
                          </Tooltip>
                        )}
                      </Box>
                    )}
                    
                    {skill?.agreed_user_count > 0 && (
                      <HStack>
                        <AvatarGroup>
                          {skill?.agreed_user
                            ?.filter?.((_, ind) => ind < 3)
                            ?.map?.((u, uind) => (
                              <Avatar
                                key={uind}
                                name={getUserFullName(u)}
                                src={getUserProfilePic(u)}
                                size="xs"
                                as={Link}
                                href={'/p/' + u?.slug}
                                onClick={(e) => e.stopPropagation()}
                              />
                            ))}
                        </AvatarGroup>
                        <Text fontSize="xs">
                          {skill?.agreed_user?.length > 3
                            ? `+${skill.agreed_user?.length - 3} `
                            : ''} 
                          {t('profileTestimonials.agreed') ||"Agreed"}
                        </Text>
                      </HStack>
                    )}
                  </HStack>
                  </ForceSignInWrapper>

                  {hoveredSkillId === skill.id && (
                    <Box
                      position="fixed"
                      w={['calc(100vw - 32px)', '400px']}
                      bg="white"
                      boxShadow="lg"
                      borderRadius="8px"
                      p={4}
                      pt={0}
                      zIndex={10}
                      style={getPopupPosition(skill.id)}
                      onMouseEnter={() => clearTimeout(timerRef.current)}
                      onMouseLeave={handleMouseLeave}
                    >
                      <Box position="relative" border="transparent">
                        <Divider mb={4} border="transparent"/>
                        <Box>
                          <Flex justifyContent="space-between" alignItems="center" mb={4}>
                            <Text fontWeight="400" fontSize="20px" color="#3D5A80" display="flex" justifyContent="center" width="100%">
                              {t('profileSkills.agreedUsers') || "Agreed Users"}
                            </Text>
                            <IconButton
                              display={"flex"}
                              icon={<SimpleCloseIcon height="14px" width="14px" />}
                              aria-label="Close"
                              variant={'unstyled'}
                              onClick={() => setHoveredSkillId(null)}
                            />
                          </Flex>
                          <Box maxHeight="300px" overflowY="auto" p={2}>
                            <VStack spacing={4} align="stretch">
                              {skill?.agreed_user?.map((user, index) => (
                                <Flex key={index} alignItems="center" justifyContent="space-between">
                                  <HStack spacing={3} cursor="pointer" onClick={() => userProfileInfo?.id === user?.id ? router.push(`/profile/me`) : router.push(`/p/${user?.slug}`)}>
                                    <Avatar src={user?.profile_pic} name={getUserFullName(user)} />
                                    <Text fontWeight="500" color="#5B5B5B" fontSize="15px">{getUserFullName(user)}</Text>
                                  </HStack>
                                  <HStack spacing={2}>
                                    <ForceSignInWrapper>
                                    {userProfileInfo?.id !== user?.id && (
                                      !isFriend(user) ? (
                                        <HStack>
                                          {isHandshakeRequested?.(user) ? (
                                            <Tooltip zIndex="99999999" label={t('publicProfile.withdrawHandshake') || "Withdraw Handshake"}>
                                              <IconButton
                                                onClick={() => handleHandshakeSendRevoke?.(user)}
                                                _hover={{ bg: 'gray.100' }}
                                                isLoading={handshakeLoading}
                                                icon={<HandShakeSimpleIcon color="#F5AEAD" width="20" height="20" />}
                                                w={'41px'}
                                                height={'41px'}
                                                border={'1px solid #00000033'}
                                                borderRadius={'50%'}
                                                variant="none"
                                              />
                                            </Tooltip>
                                          ) : (
                                            <Tooltip zIndex="99999999" label={t('publicProfile.sendHandshakeRequest') || "Send Handshake Request"}>
                                              <IconButton
                                                w={'41px'}
                                                height={'41px'}
                                                border={'1px solid #00000033'}
                                                borderRadius={'50%'}
                                                variant="none"
                                                _hover={{ bg: 'gray.100' }}
                                                onClick={() => handleHandshake(user)}
                                                isLoading={loadingButtonId === user.id}
                                                icon={<HandShakeSimpleIcon color="#037F29" width="20" height="20" />}
                                              />
                                            </Tooltip>
                                          )}
                                        </HStack>
                                      ) : (
                                        <Box
                                          w={'41px'}
                                          height={'41px'}
                                          border={'1px solid #00000033'}
                                          cursor="pointer"
                                          display="flex"
                                          alignItems="center"
                                          justifyContent="center"
                                          borderRadius={'50%'}
                                          onClick={() => {
                                            const roomId = getRoomIdFromLS(user?.id);
                                            router.push(`/profile/message?user=${roomId}`);
                                          }}
                                          _hover={{ bg: 'gray.100' }}
                                        >
                                          <ChatOutLined/>
                                        </Box>
                                      )
                                    )}
                                    </ForceSignInWrapper>
                                  </HStack>
                                </Flex>
                              ))}
                            </VStack>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  )}
                </Stack>
              );
            })}
          </HStack>

          {skills?.length > 5 && (
            <Center>
              <Button
                variant="ghost"
                justifyContent="flex-start"
                onClick={() => setShowAllSkills(!showAllSkills)}
                ps={0}
              >
                {showAllSkills ? t('publicProfile.showLess') || 'Show less' : t('publicProfile.showAll') || 'Show all'}
              </Button>
            </Center>
          )}
        </>
      ) : (
        <HStack>
          {isOwnProfile ? (
            <Link href="/profile/edit">
              {t('profileSkills.addSkills') || "Add skills to showcase your expertise"}
            </Link>
          ) : (
            <HStack opacity={0.6} pl={2}>
              <SkillIcon height="36px" width="32px" color="#868585"/>
              <Text color="#868585" pt={1.5}>
                {t('otherModules.noSkillsToShow') || "No skills to show"}
              </Text>
            </HStack>
          )}
        </HStack>
      )}

      <GlobalMatrixPopup
        isOpen={isOpen}
        onClose={onClose}
        onConfirm={confirmAction}
        selectedUserId={nullMatrixUser?.id}
      />
    </Stack>
  );
});
